import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LifeBuoy, Send, MessageSquare, User } from "lucide-react";

export default function AdminSupport() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/support/threads");
      setThreads(data);
      if (active) {
        const refreshed = data.find((t) => t.id === active.id);
        if (refreshed) setActive(refreshed);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/support/messages/${active.id}/reply`, { title: active.title, message: reply });
      setReply("");
      await load();
      toast.success("Réponse envoyée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-full">
      <PageHeader eyebrow="Support" title="PAG Helper" sub="Discussions avec les chefs et salariés. Vous êtes 'PAG Helper'." />
      <div className="p-6 lg:p-10 grid lg:grid-cols-12 gap-4">
        {/* Threads list */}
        <div className="lg:col-span-4 bg-white border border-[#DEE2E6]" data-testid="support-threads-list">
          <div className="p-4 border-b border-[#DEE2E6] text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-2">
            <LifeBuoy className="h-4 w-4 text-[#002FA7]" /> Discussions ({threads.length})
          </div>
          {threads.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#868E96]">Aucune discussion.</div>
          ) : threads.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t)}
              data-testid={`thread-${t.id}`}
              className={`w-full text-left p-4 border-b border-[#DEE2E6] last:border-b-0 transition-colors ${active?.id === t.id ? "bg-[#002FA7]/5 border-l-4 border-l-[#002FA7]" : "hover:bg-[#F8F9FA]"}`}
            >
              <div className="flex justify-between items-start gap-2">
                <div className="font-semibold text-sm truncate flex-1">{t.title}</div>
                {t.unread_for_admin && <span className="h-2 w-2 bg-[#E60000] rounded-full shrink-0 mt-1.5" />}
              </div>
              <div className="text-xs text-[#495057] mt-1 flex items-center gap-2">
                <User className="h-3 w-3" />
                <span>{t.from_name || "—"}</span>
                <span className="font-mono text-[10px] text-[#868E96]">· {t.from_role}</span>
              </div>
              <div className="text-[10px] text-[#868E96] mt-1 font-mono">{new Date(t.updated_at).toLocaleString("fr-FR")}</div>
            </button>
          ))}
        </div>

        {/* Conversation */}
        <div className="lg:col-span-8 bg-white border border-[#DEE2E6] flex flex-col min-h-[600px]" data-testid="support-conversation">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#868E96]">
              <div className="text-center">
                <MessageSquare className="h-10 w-10 mx-auto mb-3 text-[#DEE2E6]" />
                Sélectionnez une discussion à gauche.
              </div>
            </div>
          ) : (
            <>
              <div className="p-5 border-b border-[#DEE2E6]">
                <div className="text-xs uppercase tracking-[0.2em] text-[#002FA7] font-bold mb-1">{active.from_role.toUpperCase()} · {active.from_code}</div>
                <h3 className="font-display text-lg font-bold">{active.title}</h3>
                <div className="text-xs text-[#868E96] mt-1">{active.from_name}</div>
              </div>
              <div className="flex-1 p-5 space-y-3 overflow-y-auto bg-[#F8F9FA]">
                {active.messages?.map((m, i) => (
                  <div key={i} className={`flex ${m.from === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] p-3 ${m.from === "admin" ? "bg-[#002FA7] text-white" : "bg-white border border-[#DEE2E6]"}`}>
                      <div className="text-[10px] uppercase tracking-wider font-bold opacity-70 mb-1">{m.from_name}</div>
                      <div className="text-sm whitespace-pre-wrap">{m.message}</div>
                      <div className="text-[10px] opacity-60 mt-1 font-mono">{new Date(m.created_at).toLocaleString("fr-FR")}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-[#DEE2E6] flex gap-2">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Votre réponse en tant que PAG Helper…" rows={2} data-testid="admin-reply-input" className="flex-1" />
                <Button onClick={send} disabled={sending || !reply.trim()} data-testid="admin-reply-send" className="bg-[#002FA7] text-white self-end h-11">
                  <Send className="h-4 w-4 mr-2" /> Envoyer
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
